/**
 * /mi-biometria — Revocación del consentimiento biométrico como flujo
 * independiente de "eliminar cuenta".
 *
 * Alguien que quiere revocar SIN cerrar la cuenta necesita un lugar
 * evidente para hacerlo. Esta página duplica el acceso (también existe
 * en /eliminar-cuenta) y siempre muestra la consecuencia operativa
 * antes de confirmar: cuenta bloqueada + rehacer verificación.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldOff, CheckCircle, AlertTriangle } from 'lucide-react';
import { BRAND } from '../brand';
import api from '../utils/api';
import { BiometricRevokeConsequences, BIOMETRIC_CONSENT_VERSION } from '../legal/biometricConsent';

const MiBiometria = () => {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const submitRevoke = async () => {
    setRevoking(true);
    setError('');
    try {
      const r = await api.post('/biometric/consent/revoke', {
        confirm: true,
        channel: 'mi_biometria_page',
        reason: reason.trim() || null,
      });
      setResult(r.data);
      setConfirming(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(d?.message || d || 'No se pudo revocar el consentimiento.');
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate('/perfil')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 text-sm"
          data-testid="back-profile-btn"
        >
          <ArrowLeft size={16} /> Volver al perfil
        </button>

        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
              <ShieldOff size={22} className="text-slate-700" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Datos biométricos</h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Ley 21.719 · Versión aceptada:{' '}
                <code className="text-[11px]">{BIOMETRIC_CONSENT_VERSION}</code>
              </p>
            </div>
          </div>

          <p className="text-sm text-slate-700 mb-4">
            Aquí puedes revocar el consentimiento biométrico que otorgaste
            al registrarte en {BRAND.name}. <strong>No elimina tu cuenta</strong>,
            pero sí la bloquea para operar hasta que rehagas la verificación
            de identidad.
          </p>

          {result ? (
            <section
              className="rounded-2xl border-2 border-green-200 bg-green-50 p-4 sm:p-5"
              data-testid="revoke-biometric-result"
            >
              <div className="flex items-start gap-3">
                <CheckCircle size={22} className="text-green-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-green-900">
                  <p className="font-bold text-base mb-1">Consentimiento biométrico revocado</p>
                  <p>
                    Dejamos de usar tu biometría desde este momento. Tu cuenta
                    quedó bloqueada para crear nuevas órdenes. Si quieres
                    volver a operar, tendrás que rehacer la verificación de
                    identidad desde tu perfil.
                  </p>
                  {result.deletion_scheduled_at && (
                    <p className="mt-2 text-xs">
                      Fecha estimada de eliminación definitiva:{' '}
                      <strong>{result.deletion_scheduled_at}</strong>
                    </p>
                  )}
                </div>
              </div>
            </section>
          ) : (
            <>
              <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-4 mb-4 flex gap-3">
                <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <p className="font-bold mb-1">Antes de revocar, considera:</p>
                  <BiometricRevokeConsequences brandName={BRAND.name} />
                </div>
              </div>

              {!confirming ? (
                <button
                  onClick={() => setConfirming(true)}
                  className="text-sm font-bold px-5 py-3 rounded-xl bg-slate-800 text-white hover:bg-slate-900"
                  data-testid="revoke-biometric-start-btn"
                >
                  Revocar consentimiento biométrico
                </button>
              ) : (
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <p className="text-sm text-slate-800 font-semibold mb-2">
                    Confirmar revocación
                  </p>
                  <p className="text-xs text-slate-600 mb-3">
                    Al confirmar, tu cuenta quedará bloqueada para operar
                    hasta que rehagas la verificación de identidad.
                  </p>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Motivo (opcional)"
                    className="w-full text-sm px-3 py-2 border border-slate-300 rounded mb-2"
                    rows={2}
                    data-testid="revoke-biometric-reason"
                  />
                  {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
                  <div className="flex gap-2">
                    <button
                      onClick={submitRevoke}
                      disabled={revoking}
                      className="text-sm font-bold px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                      data-testid="revoke-biometric-confirm-btn"
                    >
                      {revoking ? 'Revocando…' : 'Sí, revocar y bloquear mi cuenta'}
                    </button>
                    <button
                      onClick={() => {
                        setConfirming(false);
                        setError('');
                      }}
                      className="text-sm px-4 py-2 rounded-lg bg-white border border-slate-300 hover:bg-slate-100"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MiBiometria;
